import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import { customerDateOfBirthSchema, normalizeDateOfBirth } from "../utils/dateOfBirth.mjs";
import { classScheduleNotificationConfigSchema } from "./classNotification.mjs";
import { zodFlavorId } from "./ids.mjs";
import { defineFirestoreCollection } from "./firebaseCollectionSchema.mjs";
import { PiiCategory, RedactStrategy, meta } from "./meta.mjs";
import { customerRecordingBookmarkSchema } from "./recordingBookmark.mjs";
import { customerLiveClassAttendanceSchema } from "./liveClassAttendance.mjs";
import { CustomerGender, US_CORE_BIRTHSEX_EXTENSION_URL, customerGenderDiffersFromSurvey, customerGenderSchema, customerGenderToFhirAdministrativeGender, customerGenderToUsCoreBirthsex, displayGender, genderFromSurveyAnswer } from "./gender.mjs";
import { customerAuthDisabledBySchema } from "./passwordRecoveryBridge.mjs";
import { customerNotificationDeliveryLogMonthDocSchema } from "./customerNotificationDeliveryLog.mjs";
import { subscriptionStatusChangeLogYearDocSchema } from "./subscriptionStatusChangeLog.mjs";
import { coachConsultationSchema } from "./coachConsultation.mjs";
import { userChoicesSchema } from "./userChoices.mjs";
import { z as z$1 } from "zod";

// src/models/customer.ts
let ConsultationStatus = /* @__PURE__ */ function(ConsultationStatus) {
	ConsultationStatus["SCHEDULED"] = "scheduled";
	ConsultationStatus["COMPLETED"] = "completed";
	ConsultationStatus["CANCELED"] = "canceled";
	ConsultationStatus["UNTRACKED"] = "untracked";
	ConsultationStatus["NOSHOW"] = "noshow";
	return ConsultationStatus;
}({});
const signupPaymentInfoSchema = z$1.object({
	mode: z$1.enum(["subscription", "payment"]),
	stripePriceId: zodFlavorId().nullable().default(null),
	stripeProductId: zodFlavorId().nullable().default(null),
	startDate: z$1.any().transform(zodTransformFirestoreTimestampToDate),
	endDate: z$1.any().transform(zodTransformFirestoreTimestampToDate).nullable().optional(),
	cancelled: z$1.boolean(),
	createdAt: z$1.any().transform(zodTransformFirestoreTimestampToDate),
	updatedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate)
});
const pwaInfoSchema = z$1.object({
	userAgent: meta(z$1.string(), { pii: { category: "deviceId" } }),
	installedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate),
	disabledAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional(),
	browserInfo: z$1.object({
		type: z$1.string(),
		name: z$1.string(),
		os: z$1.string(),
		version: z$1.string()
	}).optional()
}).optional();
const customerSchema = z$1.object({
	id: zodFlavorId(),
	email: meta(z$1.string(), { pii: { category: "email" } }),
	firstName: meta(z$1.string(), { pii: { category: "name" } }),
	lastName: meta(z$1.string(), { pii: { category: "name" } }),
	dateOfBirth: meta(z$1.preprocess((value) => normalizeDateOfBirth(value), customerDateOfBirthSchema.optional()), { pii: {
		category: "date",
		redact: "truncateYear"
	} }),
	consents: z$1.object({
		privacyPolicy: z$1.boolean(),
		termsOfService: z$1.boolean(),
		textMessages: z$1.boolean()
	}),
	leadQuestionAnswer: meta(z$1.string().optional(), { pii: { category: "freeText" } }),
	customerId: zodFlavorId().optional(),
	favouriteClasses: meta(z$1.array(zodFlavorId()).optional(), { bqType: "stringified_map" }),
	favouriteClassesFromSignup: meta(z$1.array(zodFlavorId()).optional(), { bqType: "stringified_map" }),
	hasEnrolled: z$1.boolean().optional(),
	signupPaymentInfo: signupPaymentInfoSchema.nullable().optional(),
	mobilePhone: meta(z$1.string().optional(), { pii: { category: "phone" } }),
	stateOfResidence: meta(z$1.string().optional(), { pii: { category: "geo" } }),
	gender: meta(customerGenderSchema.optional(), { pii: { category: "medicalId" } }),
	hearAboutUs: meta(z$1.string().optional(), { pii: { category: "freeText" } }),
	hearAboutUsProviderName: meta(z$1.string().optional(), { pii: { category: "freeText" } }),
	createdAt: z$1.any().transform(zodTransformFirestoreTimestampToDate),
	calendlyConsultationIds: meta(z$1.array(z$1.string()).optional(), {
		pii: { category: "medicalId" },
		excludeFromBqSchema: true
	}),
	/** Server-owned create/resume gate pointer — not lifecycle SSOT. */
	activeClinicalConsultationId: meta(zodFlavorId().optional(), { excludeFromBqSchema: true }),
	tellescopeId: meta(z$1.string().nullable().default(null), { pii: { category: "medicalId" } }),
	tellescopeSyncRetriesCount: meta(z$1.number().optional(), { excludeFromBqSchema: true }),
	tellescopeSyncLastErrorAt: meta(z$1.any().transform(zodTransformFirestoreTimestampToDate).nullable().optional(), { excludeFromBqSchema: true }),
	tellescopeSyncDisabled: meta(z$1.boolean().optional(), { excludeFromBqSchema: true }),
	updatedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate),
	initialBookingInfo: meta(z$1.object({
		startTime: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional(),
		coachId: z$1.string().optional(),
		bookedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional(),
		eventId: z$1.string().optional(),
		referralProgramId: zodFlavorId().optional(),
		completedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional(),
		consultationStatus: z$1.enum(ConsultationStatus).optional()
	}).nullable().optional(), { bqType: "stringified_map" }),
	consultationIncluded: z$1.boolean().optional(),
	referralProgramOrBrandingId: zodFlavorId().optional(),
	/** Missing on legacy docs — treat as false (Zod rejects explicit `undefined` for `z.boolean().optional()` in some paths). */
	signupFlowCompleted: z$1.boolean().default(false),
	/** Internal/developer accounts (auto-set at signup from email rules; admin-toggleable). */
	developerAccount: z$1.boolean().default(false),
	activityTracking: z$1.object({
		firstWebinarParticipationTime: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional(),
		firstRecordingWatchTime: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional(),
		lastWebinarParticipationTime: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional(),
		lastRecordingWatchTime: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional()
	}).optional(),
	pwaInfo: meta(z$1.object({
		ios: pwaInfoSchema,
		android: pwaInfoSchema,
		homePagePromptDismissedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).optional()
	}).optional(), { excludeFromBqSchema: true }),
	authDisabled: z$1.boolean().optional(),
	authDisabledAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date()).optional(),
	authDisabledBy: customerAuthDisabledBySchema.optional()
});
const setCustomerDeveloperAccountRequestSchema = z$1.object({
	customerId: zodFlavorId(),
	developerAccount: z$1.boolean()
});
const classScheduleCustomerDataSchema = z$1.object({
	id: zodFlavorId(),
	customerId: zodFlavorId(),
	enrolled: z$1.boolean().default(true),
	addedBy: z$1.enum([
		"admin",
		"customer",
		"migration",
		"signup"
	]).optional(),
	dismissed: z$1.boolean().optional(),
	notificationConfig: classScheduleNotificationConfigSchema.optional()
});
const classScheduleCustomerDataAuditSchema = z$1.object({
	id: zodFlavorId(),
	scheduleId: zodFlavorId(),
	customerId: zodFlavorId(),
	enrolled: z$1.boolean().default(true),
	eventType: z$1.enum([
		"create",
		"update",
		"delete"
	]),
	eventTimestamp: z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date())
});
/**
* Customers collection definition
* Path: customers/{customerId}
*/
const Customers = defineFirestoreCollection("customers", customerSchema, {
	recordingBookmarks: defineFirestoreCollection("customer_recording_bookmarks", customerRecordingBookmarkSchema, void 0, {
		dwReplication: true,
		firestoreCollectionPath: "customers/{customerId}/customer_recording_bookmarks"
	}),
	liveClassAttendance: defineFirestoreCollection("customer_live_class_attendance", customerLiveClassAttendanceSchema),
	notificationDeliveryLog: defineFirestoreCollection("customer_notification_delivery_log", customerNotificationDeliveryLogMonthDocSchema, void 0, {
		dwReplication: true,
		firestoreCollectionPath: "customers/{customerId}/customer_notification_delivery_log"
	}),
	subscriptionStatusChangeLog: defineFirestoreCollection("subscription_status_change_log", subscriptionStatusChangeLogYearDocSchema, void 0, {
		dwReplication: true,
		firestoreCollectionPath: "customers/{customerId}/subscription_status_change_log"
	}),
	coachConsultations: defineFirestoreCollection("coach_consultations", coachConsultationSchema, void 0, {
		dwReplication: true,
		firestoreCollectionPath: "customers/{customerId}/coach_consultations"
	}),
	userChoices: defineFirestoreCollection("user_choices", userChoicesSchema, void 0, {
		dwReplication: true,
		firestoreCollectionPath: "customers/{customerId}/user_choices"
	}),
	classScheduleEnrollment: defineFirestoreCollection("customer_class_schedule_enrollment", classScheduleCustomerDataSchema, { audit: defineFirestoreCollection("class_schedule_customer_data_audit", classScheduleCustomerDataAuditSchema, void 0, {
		dwReplication: true,
		firestoreCollectionPath: "customers/{customerId}/customer_class_schedule_enrollment/{scheduleId}/class_schedule_customer_data_audit"
	}) }, {
		dwReplication: true,
		firestoreCollectionPath: "customers/{customerId}/customer_class_schedule_enrollment"
	})
}, {
	dwReplication: true,
	firestoreCollectionPath: "customers"
});

export { ConsultationStatus, CustomerGender, Customers, US_CORE_BIRTHSEX_EXTENSION_URL, classScheduleCustomerDataAuditSchema, classScheduleCustomerDataSchema, customerDateOfBirthSchema, customerGenderDiffersFromSurvey, customerGenderSchema, customerGenderToFhirAdministrativeGender, customerGenderToUsCoreBirthsex, customerSchema, displayGender, genderFromSurveyAnswer, setCustomerDeveloperAccountRequestSchema, signupPaymentInfoSchema };
//# sourceMappingURL=customer.mjs.map